/**
 * The vocabulary the module editor's tabs are built from.
 *
 * @module components/studio/module-editor-model
 */

import type { EffortData } from "@/components/shared/effort-hours";
import type { ModuleWorkspaceRecord, SaveModuleWorkspaceInput } from "@/platform";
import type { Module, Programme } from "@/types";
import {
  type AssessmentWeightLedger,
  assessmentWeightLedger,
  worstEntry,
} from "@/utils/assessment-weight";
import { stableJson } from "@/utils/stable-json";

export const ASSESSMENT_TYPES = [
  "Collection of work",
  "Skills demonstration",
  "Examination",
  "Assignment",
  "Portfolio",
  "Report/Essay",
  "Project",
  "Presentation",
  "Practical/Lab",
  "Exam (On campus)",
  "Exam (Online)",
  "Reflective Journal",
  "Other",
];

export const ASSESSMENT_MODES = ["Online", "OnCampus", "Hybrid", "WorkBased"];

/** How each stored mode reads; the stored value is a code, not copy. */
export const ASSESSMENT_MODE_LABELS: Record<string, string> = {
  Online: "Online",
  OnCampus: "On campus",
  Hybrid: "Hybrid",
  WorkBased: "Work-based",
};

export const INTEGRITY_CONTROLS = [
  { key: "proctored", label: "Proctored" },
  { key: "viva", label: "Viva/oral" },
  { key: "inClass", label: "In-class component" },
  { key: "originalityCheck", label: "Originality check" },
  { key: "aiDeclaration", label: "AI declaration" },
] as const;

export const MODULE_TABS = [
  { id: "details", label: "Details", icon: "identification-card" },
  { id: "outcomes", label: "Outcomes", icon: "graduation-cap" },
  { id: "effort", label: "Effort hours", icon: "clock" },
  { id: "assessments", label: "Assessments", icon: "exam" },
  { id: "mapping", label: "Mapping", icon: "graph" },
  { id: "reading", label: "Reading", icon: "books" },
] as const;

export type ModuleTab = (typeof MODULE_TABS)[number]["id"];

export function isModuleTab(value: string | undefined): value is ModuleTab {
  return MODULE_TABS.some((tab) => tab.id === value);
}

/** The effort columns, in the order the tab renders them. */
export const EFFORT_FIELDS: {
  key: keyof EffortData;
  label: string;
  type: "number" | "text";
  placeholder?: string;
}[] = [
  { key: "classroomHours", label: "Classroom hours", type: "number" },
  { key: "classroomRatio", label: "Classroom ratio", type: "text", placeholder: "1:60" },
  { key: "mentoringHours", label: "Mentoring hours", type: "number" },
  { key: "mentoringRatio", label: "Mentoring ratio", type: "text", placeholder: "1:25" },
  { key: "otherContactHours", label: "Other contact hours", type: "number" },
  { key: "otherContactRatio", label: "Other contact ratio", type: "text", placeholder: "1:X" },
  { key: "otherContactSpecify", label: "Other contact type", type: "text" },
  { key: "directedElearningHours", label: "Directed e-learning", type: "number" },
  { key: "independentLearningHours", label: "Independent learning", type: "number" },
  { key: "otherHours", label: "Other hours", type: "number" },
  { key: "otherHoursSpecify", label: "Other hours type", type: "text" },
  { key: "workBasedHours", label: "Work-based learning", type: "number" },
];

/** The assessment weight ledger of the programme the draft sits in, read with the draft. */
export function draftWeightLedger(
  programme: Pick<Programme, "modules" | "versions"> | undefined,
  draft: Module | null,
): AssessmentWeightLedger {
  return assessmentWeightLedger(programme ?? { modules: [] }, draft ? { asIf: draft } : {});
}

/** The draft's weighting total; where versions differ, the one furthest from 100. */
export function assessmentWeightOf(ledger: AssessmentWeightLedger, draft: Module | null): number {
  return draft ? (worstEntry(ledger.entriesFor(draft.id))?.total ?? 0) : 0;
}

export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * The saved copy, with every top-level property the reader changed since
 * `sent` left for the server carried over it: the grain the server composes a
 * save on. With nothing sent, or nothing changed since, the saved copy alone.
 * Given `from`, a carried property takes its value from there instead.
 */
export function carryEdits<T extends object>(
  sent: T | undefined,
  current: T | null,
  saved: T,
  from?: T,
): T {
  if (!sent || !current || current === sent) {
    return saved;
  }
  const was = sent as Record<string, unknown>;
  const now = current as Record<string, unknown>;
  const edited = new Set(
    [...Object.keys(was), ...Object.keys(now)].filter(
      (key) => JSON.stringify(was[key]) !== JSON.stringify(now[key]),
    ),
  );
  const kept = Object.entries(saved).filter(([key]) => !edited.has(key));
  const carried = Object.entries(from ?? now).filter(([key]) => edited.has(key));
  return Object.fromEntries([...kept, ...carried]) as T;
}

/**
 * A save's answer as the next save's base, with each property typed while it
 * was out kept at the value it was typed over: the answer may carry a peer's
 * value there that this reader never saw.
 */
export function rebaseCarried(
  next: ModuleWorkspaceRecord,
  sent: SaveModuleWorkspaceInput,
  typed: { module: Module | null; ploToMimlos: Record<string, string[]> },
): ModuleWorkspaceRecord {
  return {
    ...next,
    module: carryEdits(sent.module, typed.module, next.module, sent.module),
    ploToMimlos: carryEdits(
      sent.ploToMimlos,
      typed.ploToMimlos,
      next.ploToMimlos,
      sent.ploToMimlos,
    ),
  };
}

/**
 * What "dirty" is measured against: the module and its mappings together. Key
 * order is not a change, and an outcome mapped to nothing is the same as one
 * absent: storage keeps no row for either, and a read answers without the key.
 */
export function serialized(module: Module, ploToMimlos: Record<string, string[]>): string {
  const mapped = Object.fromEntries(
    Object.entries(ploToMimlos).filter(([, mimloIds]) => mimloIds.length > 0),
  );
  return stableJson({ module, ploToMimlos: mapped });
}

/**
 * Whether the reader would take the module from the builder a review returned
 * it to. Only a reader known not to own it: an unknown reader keeps the lease.
 */
export function returnedToBuilder(record: ModuleWorkspaceRecord, readerUserId?: string): boolean {
  return (
    record.canEdit &&
    record.moduleReview.status === "Changes Requested" &&
    readerUserId !== undefined &&
    Boolean(record.ownerUserId) &&
    record.ownerUserId !== readerUserId
  );
}

/** What was thrown, as an Error, so a notice always has a message to show. */
export function asError(cause: unknown, fallback: string): Error {
  return cause instanceof Error ? cause : new Error(fallback);
}
